import { useState, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  useAccessKeys, useCalls, useDeleteRecording, useMe, usePhones, streamUrl,
} from '../api'
import type { CallOutcome } from '../api/hooks'
import { AudioPlayer } from '../components/AudioPlayer'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { Pagination } from '../components/Pagination'
import { RecordingActionsMenu } from '../components/RecordingActionsMenu'
import { RecordingStatusBadge } from '../components/RecordingStatusBadge'
import { SearchableSelect } from '../components/SearchableSelect'
import { useNotify } from '../components/Notifications'
import { HelpButton, StatusesHelpModal } from '../components/StatusesHelpModal'
import { getReasonDisplay } from '../config/callReasons'
import type { CallRecord, CallQuality } from '../types'
import { formatDateTime, formatDuration } from '../lib/format'
import { computeQualityFromRecord, qualityLabels, qualityTooltip } from '../lib/callQuality'
import { SignalBars } from '../components/SignalBars'

function ReasonBadge({ call }: { call: CallRecord }) {
  const config = getReasonDisplay(call.reason, call.direction, call.duration_seconds)
  return (
    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded ${config.color} ${config.bg}`}>
      {config.label}
    </span>
  )
}

function DirectionIcon({ direction }: { direction: string }) {
  if (direction === 'outgoing') {
    return (
      <svg className="w-4 h-4 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="7" y1="17" x2="17" y2="7" /><polyline points="7 7 17 7 17 17" />
      </svg>
    )
  }
  return (
    <svg className="w-4 h-4 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="17" y1="7" x2="7" y2="17" /><polyline points="17 17 7 17 7 7" />
    </svg>
  )
}

function QualityCell({ quality }: { quality: CallQuality | null }) {
  const level = computeQualityFromRecord(quality)
  const tooltip = qualityTooltip(quality)

  if (level === null) {
    return <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
  }

  const { label, color } = qualityLabels[level]

  return (
    <span title={tooltip} className="inline-flex items-center gap-1.5 cursor-default">
      <SignalBars level={level} size="sm" className={color} />
      <span className={`text-xs font-medium ${color}`}>{label}</span>
    </span>
  )
}

/**
 * Inline recording controls for a Call row. Branches:
 *   - ready                             → player + actions, no badge
 *   - partial_sim / partial_op          → player (one surviving track)
 *                                          + amber badge + actions
 *   - incomplete                        → player (canonical playable)
 *                                          + orange badge + actions
 *   - merge_failed                      → badge only + actions
 *   - null / deleted / unknown          → em-dash
 *
 * All actions key off call.call_id. streamUrl + useDeleteRecording are
 * the same helpers used on /staff/recordings. useDeleteRecording
 * invalidates both ['recordings'] and ['calls'] on success so the row
 * refreshes here too.
 */
function RecordingCell({
  call,
  onRequestDelete,
  compact = false,
}: {
  call: CallRecord
  onRequestDelete: (callId: string) => void
  compact?: boolean
}) {
  const rec = call.recording
  if (!rec || rec.status === 'deleted') {
    return <span className="text-xs text-gray-400 dark:text-gray-600">—</span>
  }
  const playable =
    rec.status === 'ready' ||
    rec.status === 'partial_sim' ||
    rec.status === 'partial_op' ||
    rec.status === 'incomplete'
  const showBadge = rec.status !== 'ready'
  const url = playable ? streamUrl(call.call_id) : null
  // Stack only when the player is present in compact mode — AudioPlayer
  // needs full width on a mobile card. Badges are small; keep them inline.
  const stack = playable && compact
  return (
    <div className={stack ? 'space-y-2' : 'flex items-center gap-2'}>
      {playable && (
        <AudioPlayer src={url!} testId={`rec-audio-${call.call_id}`} controlsTestId={call.call_id} />
      )}
      {showBadge && <RecordingStatusBadge status={rec.status} />}
      <RecordingActionsMenu
        callId={call.call_id}
        streamUrl={url}
        onRequestDelete={onRequestDelete}
        triggerTestId={`rec-menu-${call.call_id}`}
        downloadTestId={`rec-download-${call.call_id}`}
        deleteTestId={`rec-delete-${call.call_id}`}
      />
    </div>
  )
}

function CallRow({
  call,
  onRequestDelete,
}: {
  call: CallRecord
  onRequestDelete: (callId: string) => void
}) {
  const from = call.direction === 'outgoing' ? call.phone_sim : call.number
  const to = call.direction === 'outgoing' ? call.number : call.phone_sim

  return (
    <tr className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900/50">
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
        {formatDateTime(call.ended_at)}
      </td>
      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap max-w-[14rem] truncate">
        {call.phone_name || <span className="text-gray-400 dark:text-gray-600">—</span>}
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="flex items-center gap-2">
          <DirectionIcon direction={call.direction} />
          <div className="text-sm">
            <span className="text-gray-500 dark:text-gray-400">{from}</span>
            <span className="text-gray-300 dark:text-gray-600 mx-1.5">&rarr;</span>
            <span className="text-gray-900 dark:text-gray-100 font-medium">{to}</span>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
        {call.attempts}
      </td>
      <td className="px-4 py-3 font-mono text-sm text-gray-600 dark:text-gray-300 text-right">
        {formatDuration(call.duration_seconds)}
      </td>
      <td className="px-4 py-3">
        <QualityCell quality={call.quality} />
      </td>
      <td className="px-4 py-3">
        <RecordingCell call={call} onRequestDelete={onRequestDelete} />
      </td>
      <td className="px-4 py-3">
        <ReasonBadge call={call} />
      </td>
    </tr>
  )
}

/** Mobile card layout for small screens. */
function CallCard({
  call,
  onRequestDelete,
}: {
  call: CallRecord
  onRequestDelete: (callId: string) => void
}) {
  const from = call.direction === 'outgoing' ? call.phone_sim : call.number
  const to = call.direction === 'outgoing' ? call.number : call.phone_sim

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <DirectionIcon direction={call.direction} />
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{to}</span>
        </div>
        <ReasonBadge call={call} />
      </div>
      {call.phone_name && (
        <div className="text-xs text-gray-500 dark:text-gray-400">
          Трубка: <span className="text-gray-700 dark:text-gray-200">{call.phone_name}</span>
        </div>
      )}
      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <span>{from} &rarr; {to}</span>
        <span className="font-mono">{formatDuration(call.duration_seconds)}</span>
      </div>
      <div className="flex items-center justify-between text-xs text-gray-400 dark:text-gray-500">
        <span>{formatDateTime(call.ended_at)}</span>
        <span>{call.attempts} attempt{call.attempts !== 1 ? 's' : ''}</span>
      </div>
      <div className="text-xs">
        <QualityCell quality={call.quality} />
      </div>
      {call.recording && call.recording.status !== 'deleted' && (
        <div className="pt-2 border-t border-gray-100 dark:border-gray-800">
          <RecordingCell call={call} onRequestDelete={onRequestDelete} compact />
        </div>
      )}
    </div>
  )
}

const dtInputCls =
  'dt-input rounded-lg border border-gray-300 dark:border-gray-700 ' +
  'bg-white dark:bg-gray-900 px-3 py-2 text-sm ' +
  'text-gray-700 dark:text-gray-300 ' +
  'hover:border-gray-400 dark:hover:border-gray-600 ' +
  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ' +
  'transition-colors'

export function CallHistoryPage() {
  const { data: phones = [] } = usePhones()
  const { data: me } = useMe()
  const isAdmin = me?.role === 'admin'
  // Access keys — source for the admin-only operator dropdown. The hook
  // is safe to call for non-admins (returns empty array on 403); we only
  // render the dropdown when isAdmin so the request latency doesn't
  // bother anyone else.
  const { data: accessKeys = [] } = useAccessKeys()
  // `?phone=<id>` seeds the phone filter once on mount — the heatmap board
  // links here with it. The URL is read synchronously (not async like the
  // phones list), so a lazy initializer is safe; afterwards it's plain
  // local state, unsynced back to the URL like every other filter here.
  const [searchParams] = useSearchParams()
  const [phoneFilter, setPhoneFilter] = useState(() => searchParams.get('phone') ?? '')
  const [userFilter, setUserFilter] = useState('')
  // Filter dropdown encodes both `reason` and synthetic `outcome` filters in
  // a single value with a prefix: `reason:busy`, `outcome:answered`, ''=all.
  // The backend rejects sending both at once with 400; UI guarantees only one.
  const [statusFilter, setStatusFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [pendingDeleteCallId, setPendingDeleteCallId] = useState<string | null>(null)
  const [helpOpen, setHelpOpen] = useState(false)

  const notify = useNotify()
  const deleteRecording = useDeleteRecording()

  // Rate limit: min 1s between searches
  const lastSearchRef = useRef(0)
  const doSearch = () => {
    const now = Date.now()
    if (now - lastSearchRef.current < 1000) return
    lastSearchRef.current = now
    setSearchQuery(searchInput.trim())
    setPage(1)
  }

  const clearSearch = () => {
    setSearchInput('')
    setSearchQuery('')
    setPage(1)
  }

  const [statusKind, statusValue] = statusFilter.split(':')
  // <input type="datetime-local"> emits "YYYY-MM-DDTHH:mm" (no seconds, local TZ).
  // Backend filters Call.ended_at <= date_to (call.py:96). For the "to" boundary
  // we extend to end-of-minute (:59.999) so a user picking "14:30" includes the
  // entire 14:30 minute. toISOString() converts local time to UTC for transport.
  const dateFromISO = dateFrom ? new Date(`${dateFrom}:00.000`).toISOString() : undefined
  const dateToISO = dateTo ? new Date(`${dateTo}:59.999`).toISOString() : undefined
  const { data, isLoading, isFetching, error } = useCalls({
    phoneId: phoneFilter || undefined,
    userId: isAdmin ? (userFilter || undefined) : undefined,
    reason: statusKind === 'reason' ? statusValue : undefined,
    outcome: statusKind === 'outcome' ? (statusValue as CallOutcome) : undefined,
    search: searchQuery || undefined,
    dateFrom: dateFromISO,
    dateTo: dateToISO,
    page,
    pageSize,
  })

  const totalPages = data ? Math.ceil(data.total / pageSize) : 0

  const confirmDeleteRecording = () => {
    if (!pendingDeleteCallId) return
    deleteRecording.mutate(pendingDeleteCallId, {
      onSuccess: () => {
        notify('Запись удалена', 'info')
        setPendingDeleteCallId(null)
      },
      onError: err => notify(err.message || 'Ошибка удаления', 'error'),
    })
  }

  return (
    <div className="max-w-screen-2xl mx-auto px-4 py-6">
      <div className="flex items-center gap-2 mb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Call History</h2>
        <HelpButton onClick={() => setHelpOpen(true)} />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-1.5">
          <input
            type="text"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') doSearch() }}
            placeholder="Search by number..."
            maxLength={30}
            className="w-44 md:w-56 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500 hover:border-gray-400 dark:hover:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
          />
          <button
            onClick={doSearch}
            className="px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
          >
            Search
          </button>
          {searchQuery && (
            <button
              onClick={clearSearch}
              className="px-2 py-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-sm transition-colors"
            >
              Clear
            </button>
          )}
        </div>

        <SearchableSelect
          options={phones.map(p => ({ value: p.id, label: p.name, sublabel: p.sim }))}
          value={phoneFilter}
          onChange={v => { setPhoneFilter(v); setPage(1) }}
          allLabel="All phones"
          searchPlaceholder="Поиск по имени или SIM"
          testId="phone-filter"
        />

        {isAdmin && (
          <SearchableSelect
            options={accessKeys.map(k => ({ value: k.id, label: k.label || 'key без метки' }))}
            value={userFilter}
            onChange={v => { setUserFilter(v); setPage(1) }}
            allLabel="All operators"
            searchPlaceholder="Поиск оператора"
            testId="operator-filter"
          />
        )}

        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1) }}
          className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-700 dark:text-gray-300"
          data-testid="status-filter"
        >
          <option value="">Все статусы</option>
          <optgroup label="Действия оператора">
            <option value="outcome:answered">Отвечен</option>
            <option value="outcome:rejected">Сброс менеджером (входящий)</option>
            <option value="outcome:cancelled">Сброс менеджером (исходящий)</option>
          </optgroup>
          <optgroup label="Реакция абонента">
            <option value="reason:busy">Занято</option>
            <option value="reason:no_answer">Не отвечает</option>
            <option value="reason:rejected">Сброс на той стороне</option>
            <option value="reason:remote_hangup">Удалённый сброс</option>
            <option value="reason:restricted">Ограничение оператора</option>
            <option value="reason:answered_elsewhere">Отвечен на другом</option>
          </optgroup>
          <optgroup label="Проблема трубки">
            <option value="reason:no_sim">Нет SIM</option>
            <option value="reason:no_network">Нет сети</option>
          </optgroup>
          <optgroup label="Технический сбой">
            <option value="reason:webrtc_failed">Сбой WebRTC</option>
            <option value="reason:sync_timeout">Сбой синхронизации</option>
            <option value="reason:agent_disconnected">Агент отключился</option>
            <option value="reason:error">Ошибка</option>
          </optgroup>
          <optgroup label="Админ-действия">
            <option value="reason:phone_revoked">Телефон отозван</option>
            <option value="reason:operator_revoked">Доступ отозван</option>
            <option value="reason:browser_replaced">Сессия заменена</option>
          </optgroup>
        </select>

        <select
          value={pageSize}
          onChange={e => { setPageSize(Number(e.target.value)); setPage(1) }}
          className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-700 dark:text-gray-300"
        >
          <option value={20}>20 per page</option>
          <option value={50}>50 per page</option>
          <option value={100}>100 per page</option>
        </select>

        {data && (
          <span className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 ml-auto">
            {isFetching && !isLoading && (
              <span className="w-3 h-3 border-2 border-gray-300 dark:border-gray-600 border-t-blue-500 rounded-full animate-spin" />
            )}
            {data.total} call{data.total !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Date range — separate row so it doesn't crowd the main filter line */}
      <div className="flex flex-wrap items-center gap-1.5 mb-4">
        <span className="text-xs font-mono uppercase tracking-wider text-gray-500 dark:text-gray-400 mr-2">
          Период
        </span>
        <input
          type="datetime-local"
          value={dateFrom}
          onChange={e => { setDateFrom(e.target.value); setPage(1) }}
          aria-label="Дата и время от"
          className={dtInputCls}
          data-testid="date-from-filter"
        />
        <span className="text-gray-400 text-sm">—</span>
        <input
          type="datetime-local"
          value={dateTo}
          onChange={e => { setDateTo(e.target.value); setPage(1) }}
          aria-label="Дата и время до"
          className={dtInputCls}
          data-testid="date-to-filter"
        />
        {(dateFrom || dateTo) && (
          <button
            onClick={() => { setDateFrom(''); setDateTo(''); setPage(1) }}
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:text-red-500 hover:border-red-300 dark:hover:text-red-400 dark:hover:border-red-700 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
            aria-label="Очистить период"
            data-testid="date-clear"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </svg>
          </button>
        )}
      </div>

      {/* Loading */}
      {isLoading && (
        <p className="text-gray-400 dark:text-gray-500 text-center py-12">Loading...</p>
      )}

      {/* Error */}
      {error && (
        <p className="text-red-500 text-center py-12">{error.message}</p>
      )}

      {/* Empty state */}
      {data && data.items.length === 0 && (
        <p className="text-gray-400 dark:text-gray-500 text-center py-12">No calls found</p>
      )}

      {/* Desktop table */}
      {data && data.items.length > 0 && (
        <>
          {totalPages > 1 && (
            <div className="mb-4">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>
          )}
          <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  <th className="px-4 py-3 text-left">Date</th>
                  <th className="px-4 py-3 text-left">Трубка</th>
                  <th className="px-4 py-3 text-left">From / To</th>
                  <th className="px-4 py-3 text-center">Tries</th>
                  <th className="px-4 py-3 text-right">Duration</th>
                  <th className="px-4 py-3 text-left">Quality</th>
                  <th className="px-4 py-3 text-left">Запись</th>
                  <th className="px-4 py-3 text-left">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-950">
                {data.items.map(call => (
                  <CallRow
                    key={call.id}
                    call={call}
                    onRequestDelete={setPendingDeleteCallId}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {data.items.map(call => (
              <CallCard
                key={call.id}
                call={call}
                onRequestDelete={setPendingDeleteCallId}
              />
            ))}
          </div>

          <ConfirmDialog
            open={pendingDeleteCallId !== null}
            title="Удалить запись звонка?"
            message={
              pendingDeleteCallId
                ? `Запись звонка ${pendingDeleteCallId} будет удалена с диска без возможности восстановления.`
                : ''
            }
            confirmLabel="Удалить"
            isPending={deleteRecording.isPending}
            onConfirm={confirmDeleteRecording}
            onClose={() => setPendingDeleteCallId(null)}
          />

          {totalPages > 1 && (
            <div className="mt-6">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>
          )}
        </>
      )}

      <StatusesHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  )
}
